import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  DocumentTextIcon,
  UserIcon,
  CalendarIcon,
  PencilIcon,
  BriefcaseIcon,
  EyeIcon,
  ClockIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';
import {
  fetchClaimNumberDuplicate,
  showDuplicateAlert,
  requestDuplicateConfirm
} from '../utils/duplicateClaimChecks';
import DuplicateWarningModal from '../components/DuplicateWarningModal';
import AdjusterConfirmModal from '../components/AdjusterConfirmModal';
import { useViewExistingClaim } from '../hooks/useViewExistingClaim';
import { 
  getJobStatusColor, 
  getJobStatusLabel,
  mapStatusForCustomer
} from '../utils/statusConstants';
import { navigateBack } from '../components/RoutePersistence';

const ClaimDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { makeAuthenticatedRequest, customer } = useAuth();
  const {
    viewClaim,
    pendingClaim,
    promptIfNeeded,
    saveAssignment,
    skipPrompt,
    saving: savingRole
  } = useViewExistingClaim(makeAuthenticatedRequest, customer);
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [claimants, setClaimants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  const [formData, setFormData] = useState({
    claimant_id: '',
    case_type: 'Workers Compensation',
    claim_number: '',
    file_status: 'Open',
    date_of_injury: '',
    ct_start_date: '',
    ct_end_date: '',
    diagnosis: '',
    employer_insured: '',
    contact_claims_handler_id: '',
    adjusters_assistant_id: '',
    nurse_case_manager_id: '',
    field_case_manager_id: ''
  });
  const [claimNumberWarning, setClaimNumberWarning] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null);

  const caseTypeOptions = [
    { value: 'Workers Compensation', label: 'Workers Compensation' },
    { value: 'Personal Injury', label: 'Personal Injury' },
    { value: 'Medical Malpractice', label: 'Medical Malpractice' },
    { value: 'Auto Accident', label: 'Auto Accident' },
    { value: 'Slip and Fall', label: 'Slip and Fall' },
    { value: 'Product Liability', label: 'Product Liability' },
    { value: 'Other', label: 'Other' }
  ];

  useEffect(() => {
    if (id) {
      loadClaimDetails();
      loadClaimants();
      loadCustomers();
      loadRelatedJobs();
    }
  }, [id]);

  useEffect(() => {
    if (!loading && claim) {
      promptIfNeeded(claim, { onUpdated: loadClaimDetails });
    }
  }, [loading, claim?.id]);

  const loadClaimDetails = async () => {
    try {
      setLoading(true);
      const data = await makeAuthenticatedRequest(`/claims/${id}`);
      
      if (data && data.success && data.data) {
        const claimData = data.data;
        setClaim(claimData);
        setFormData({
          claimant_id: claimData.claimant_id ? claimData.claimant_id.toString() : '',
          case_type: claimData.case_type || 'Workers Compensation',
          claim_number: claimData.claim_number || '',
          file_status: claimData.file_status || 'Open',
          date_of_injury: formatDateForInput(claimData.date_of_injury),
          ct_start_date: formatDateForInput(claimData.ct_start_date),
          ct_end_date: formatDateForInput(claimData.ct_end_date),
          diagnosis: claimData.diagnosis || '',
          employer_insured: claimData.employer_insured || '',
          contact_claims_handler_id: claimData.contact_claims_handler_id ? claimData.contact_claims_handler_id.toString() : '',
          adjusters_assistant_id: claimData.adjusters_assistant_id ? claimData.adjusters_assistant_id.toString() : '',
          nurse_case_manager_id: claimData.nurse_case_manager_id ? claimData.nurse_case_manager_id.toString() : '',
          field_case_manager_id: claimData.field_case_manager_id ? claimData.field_case_manager_id.toString() : ''
        });
      } else {
        toast.error(data?.message || 'Failed to load claim details');
      }
    } catch (error) {
      console.error('Error loading claim details:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load claim details'));
    } finally {
      setLoading(false);
    }
  };

  const loadClaimants = async () => {
    try {
      const data = await makeAuthenticatedRequest('/claimants');
      if (data && data.success) {
        setClaimants(data.data || []);
      }
    } catch (error) {
      console.error('Error loading claimants:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await makeAuthenticatedRequest('/customers');
      if (data && data.success) {
        setCustomers(data.data || []);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadRelatedJobs = async () => {
    try {
      setLoadingJobs(true);
      const data = await makeAuthenticatedRequest(`/appointments?claimId=${id}&limit=50`);
      if (data && data.success) {
        setRelatedJobs(data.data.appointments || []);
      }
    } catch (error) {
      console.error('Error loading related jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // If the date is already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Otherwise parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // Extract date parts to avoid timezone conversion
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      
      // Create date in local timezone (no UTC conversion)
      const localDate = new Date(year, month - 1, day);
      
      if (isNaN(localDate.getTime())) {
        return 'N/A';
      }
      
      return localDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'claim_number') setClaimNumberWarning(null);
  };

  const checkClaimNumberOnBlur = async () => {
    const num = (formData.claim_number || '').trim();
    if (!num) { setClaimNumberWarning(null); return; }
    const dup = await fetchClaimNumberDuplicate(makeAuthenticatedRequest, num, claim?.id || id);
    setClaimNumberWarning(dup);
    showDuplicateAlert(setDuplicateModal, 'claim', dup);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.claim_number || !formData.case_type) {
      toast.error('Claim number and case type are required');
      return;
    }

    setSaving(true);
    try {
      const claimDup = await fetchClaimNumberDuplicate(makeAuthenticatedRequest, formData.claim_number, id);
      setClaimNumberWarning(claimDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claim', claimDup))) {
        setSaving(false);
        return;
      }

      const data = await makeAuthenticatedRequest(`/claims/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...formData,
          claimant_id: formData.claimant_id ? parseInt(formData.claimant_id) : null,
          contact_claims_handler_id: formData.contact_claims_handler_id || null,
          adjusters_assistant_id: formData.adjusters_assistant_id || null,
          nurse_case_manager_id: formData.nurse_case_manager_id || null,
          field_case_manager_id: formData.field_case_manager_id || null,
          date_of_injury: formData.date_of_injury || null,
          ct_start_date: formData.ct_start_date || null,
          ct_end_date: formData.ct_end_date || null,
          diagnosis: formData.diagnosis || null,
          employer_insured: formData.employer_insured || null,
          file_status: formData.file_status || 'Open'
        })
      });

      if (data && data.success) {
        toast.success('Claim updated successfully');
        setIsEditing(false);
        await loadClaimDetails();
      } else {
        toast.error(data.message || 'Failed to update claim');
      }
    } catch (error) {
      console.error('Error updating claim:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to update claim'));
    } finally {
      setSaving(false);
    }
  };

  const getClaimantName = (claim) => {
    if (claim.claimant_first_name && claim.claimant_last_name) {
      return `${claim.claimant_first_name} ${claim.claimant_last_name}`;
    }
    return claim.claimant_name || 'No Claimant Assigned';
  };

  const handleRequestAppointment = () => {
    const params = new URLSearchParams();
    if (claim.id) params.set('claimId', claim.id);
    if (claim.claimant_id) params.set('claimantId', claim.claimant_id);
    navigate(`/appointments/new?${params.toString()}`);
  };

  const handleDeleteClaim = async () => {
    if (!claim?.id || deleting) return;

    const confirmed = window.confirm(
      `Delete claim ${claim.claim_number}? This action can’t be undone.`
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      const data = await makeAuthenticatedRequest(`/claims/${claim.id}`, {
        method: 'DELETE'
      });

      if (data?.success) {
        toast.success('Claim deleted successfully');
        navigate('/claims');
        return;
      }

      toast.error(data?.message || 'Failed to delete claim');
    } catch (error) {
      console.error('Error deleting claim:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to delete claim'));
    } finally {
      setDeleting(false);
    }
  };

  // Simple SearchableSelect component
  const SearchableSelect = ({ value, onChange, options = [], placeholder, className = '', label, required = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options || []);

    useEffect(() => {
      if (!options || !Array.isArray(options)) {
        setFilteredOptions([]);
        return;
      }
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }, [searchTerm, options]);

    const handleSelect = (option) => {
      onChange(option.value);
      setIsOpen(false);
      setSearchTerm('');
    };

    return (
      <div className={className}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}
        <div className="relative">
          <input
            type="text"
            value={searchTerm || (options.find(o => o.value === value)?.label || '')}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {isOpen && filteredOptions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Claim not found</p>
          <button
            onClick={() => navigateBack(navigate, location.pathname, '/claims')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigateBack(navigate, location.pathname, '/claims')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          title="Go back to previous page"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 mr-3 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit Claim' : `Claim: ${claim.claim_number}`}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {isEditing ? 'Update claim information' : 'View and manage claim details'}
              </p>
            </div>
          </div>
          {!isEditing && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRequestAppointment}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CalendarIcon className="h-5 w-5 mr-2" />
                Request Appointment
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                Edit Claim
              </button>
              <button
                onClick={handleDeleteClaim}
                disabled={deleting}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                {deleting ? 'Deleting...' : 'Delete Claim'}
              </button>
            </div>
          )}
        </div>
      </div>

      {isEditing ? (
        /* Edit Form */
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          {/* Claimant Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Claimant
            </label>
            <SearchableSelect
              options={claimants.map(c => {
                const name = c.first_name && c.last_name 
                  ? `${c.first_name} ${c.last_name}`
                  : c.name || 'Unnamed Claimant';
                const dob = c.date_of_birth 
                  ? (() => {
                      const datePart = (typeof c.date_of_birth === 'string' ? c.date_of_birth : '').split('T')[0];
                      if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return '';
                      const [y, m, d] = datePart.split('-').map((n) => parseInt(n, 10));
                      return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
                    })()
                  : '';
                const dobText = dob ? ` - DOB: ${dob}` : '';
                return {
                  value: c.id.toString(),
                  label: `${name}${dobText}`
                };
              })}
              value={formData.claimant_id}
              onChange={(value) => handleInputChange('claimant_id', value)}
              placeholder="Select a claimant..."
            />
          </div>

          {/* Claim Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Claim Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.claim_number}
              onChange={(e) => handleInputChange('claim_number', e.target.value)}
              onBlur={checkClaimNumberOnBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Case Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Case Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.case_type}
              onChange={(e) => handleInputChange('case_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {caseTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* File Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Status
            </label>
            <select
              value={formData.file_status}
              onChange={(e) => handleInputChange('file_status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
            </select>
          </div>

          {/* Date of Injury */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Injury
            </label>
            <input
              type="date"
              value={formData.date_of_injury}
              onChange={(e) => handleInputChange('date_of_injury', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* CT Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CT Start Date
            </label>
            <input
              type="date"
              value={formData.ct_start_date}
              onChange={(e) => handleInputChange('ct_start_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* CT End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CT End Date
            </label>
            <input
              type="date"
              value={formData.ct_end_date}
              onChange={(e) => handleInputChange('ct_end_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Diagnosis */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Diagnosis
            </label>
            <textarea
              value={formData.diagnosis}
              onChange={(e) => handleInputChange('diagnosis', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Employer/Insured */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employer/Insured
            </label>
            <input
              type="text"
              value={formData.employer_insured}
              onChange={(e) => handleInputChange('employer_insured', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter employer or insured party name (optional)"
            />
          </div>

          {/* Contact Claims Handler */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact/Claims Handler
            </label>
            <SearchableSelect
              options={customers.map(c => ({
                value: c.id.toString(),
                label: c.name || 'Unnamed Customer'
              }))}
              value={formData.contact_claims_handler_id}
              onChange={(value) => handleInputChange('contact_claims_handler_id', value)}
              placeholder="Select contact/claims handler..."
            />
          </div>

          {/* Adjuster's Assistant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adjuster's Assistant
            </label>
            <SearchableSelect
              options={customers.map(c => ({
                value: c.id.toString(),
                label: c.name || 'Unnamed Customer'
              }))}
              value={formData.adjusters_assistant_id}
              onChange={(value) => handleInputChange('adjusters_assistant_id', value)}
              placeholder="Select adjuster's assistant..."
            />
          </div>

          {/* Nurse Case Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nurse Case Manager
            </label>
            <SearchableSelect
              options={customers.map(c => ({
                value: c.id.toString(),
                label: c.name || 'Unnamed Customer'
              }))}
              value={formData.nurse_case_manager_id}
              onChange={(value) => handleInputChange('nurse_case_manager_id', value)}
              placeholder="Select nurse case manager..."
            />
          </div>

          {/* Field Case Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Field Case Manager
            </label>
            <SearchableSelect
              options={customers.map(c => ({
                value: c.id.toString(),
                label: c.name || 'Unnamed Customer'
              }))}
              value={formData.field_case_manager_id}
              onChange={(value) => handleInputChange('field_case_manager_id', value)}
              placeholder="Select field case manager..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                loadClaimDetails();
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      ) : (
        /* View Mode */
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          {/* Claim Information */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Claim Number</label>
              <p className="text-lg font-semibold text-gray-900">{claim.claim_number}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Case Type</label>
              <p className="text-lg text-gray-900">{claim.case_type || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">File Status</label>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                  claim.file_status === 'Closed'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-green-100 text-green-800'
                }`}
              >
                {claim.file_status || 'Open'}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Claimant</label>
              {claim.claimant_id ? (
                <button
                  onClick={() => navigate(`/claimants?claimantId=${claim.claimant_id}`)}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {getClaimantName(claim)}
                </button>
              ) : (
                <p className="text-gray-500 italic">No claimant assigned</p>
              )}
            </div>
            {claim.claimant_phone && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Claimant Phone</label>
                <div className="flex items-center">
                  <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <p className="text-gray-900">{claim.claimant_phone}</p>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Date of Injury</label>
              <p className="text-gray-900">{formatDate(claim.date_of_injury)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">CT Start Date</label>
              <p className="text-gray-900">{formatDate(claim.ct_start_date)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">CT End Date</label>
              <p className="text-gray-900">{formatDate(claim.ct_end_date)}</p>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-500 mb-1">Diagnosis</label>
              <p className="text-gray-900 whitespace-pre-wrap">{claim.diagnosis || 'N/A'}</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Contact/Claims Handler</label>
                <p className="text-gray-900">{claim.contact_claims_handler_name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Adjuster's Assistant</label>
                <p className="text-gray-900">{claim.adjusters_assistant_name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Nurse Case Manager</label>
                <p className="text-gray-900">{claim.nurse_case_manager_name || 'N/A'}</p>
                {claim.nurse_case_manager_phone && (
                  <p className="text-sm text-gray-600 mt-1">{claim.nurse_case_manager_phone}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Field Case Manager</label>
                <p className="text-gray-900">{claim.field_case_manager_name || 'N/A'}</p>
                {claim.field_case_manager_phone && (
                  <p className="text-sm text-gray-600 mt-1">{claim.field_case_manager_phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Related Jobs */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <BriefcaseIcon className="h-5 w-5 mr-2 text-indigo-600" />
                Related Jobs
              </h3>
              <span className="text-sm text-gray-500">
                {relatedJobs.length} {relatedJobs.length === 1 ? 'job' : 'jobs'}
              </span>
            </div>
            
            {loadingJobs ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : relatedJobs.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <BriefcaseIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500 text-sm mb-3">No appointments found for this claim</p>
                <button
                  onClick={handleRequestAppointment}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Request First Appointment
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {relatedJobs.map((job) => (
                  <div
                    key={job.id}
                    onClick={() => navigate(`/appointments/${job.id}`)}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors border border-gray-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900 mr-3">
                          {job.job_number || `Appointment #${job.id}`}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getJobStatusColor(job)}`}>
                          {getJobStatusLabel(job)}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        <span>
                          {job.scheduled_date ? (() => {
                            const dateStr = job.scheduled_date;
                            if (dateStr.includes('-') && !dateStr.includes('T')) {
                              const [year, month, day] = dateStr.split('-').map(Number);
                              return new Date(year, month - 1, day).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              });
                            }
                            return new Date(dateStr).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            });
                          })() : 'Not scheduled'}
                        </span>
                        {job.scheduled_time && (
                          <>
                            <ClockIcon className="h-4 w-4 ml-3 mr-1" />
                            <span>
                              {new Date(`2000-01-01T${job.scheduled_time}`).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </>
                        )}
                      </div>
                      {job.appointment_type && (
                        <p className="mt-1 text-sm text-gray-500">
                          {job.appointment_type}
                        </p>
                      )}
                    </div>
                    <EyeIcon className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="border-t pt-6">
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Created</label>
                <p className="text-gray-900">{formatDate(claim.created_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                <p className="text-gray-900">{formatDate(claim.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <DuplicateWarningModal
        isOpen={!!duplicateModal}
        type={duplicateModal?.type}
        data={duplicateModal?.data}
        mode={duplicateModal?.mode}
        onClose={() => {
          duplicateModal?.onResolve?.(false);
          setDuplicateModal(null);
        }}
        onConfirm={() => {
          duplicateModal?.onResolve?.(true);
          setDuplicateModal(null);
        }}
        onViewClaimant={(id) => navigate(`/claimants?claimantId=${id}`)}
        onViewClaim={viewClaim}
      />

      <AdjusterConfirmModal
        isOpen={!!pendingClaim}
        claim={pendingClaim}
        onSave={saveAssignment}
        onSkip={skipPrompt}
        saving={savingRole}
      />
    </div>
  );
};

export default ClaimDetails;




